/**
 * MoodMap Zustand Store
 *
 * State management for the MoodMap daily reflection feature.
 * Handles entries, items, tags, AI parsing, and UI state.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { moodmap } from '../services/supabaseService';
import { parseMoodMapInput } from '../services/geminiService';
import {
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  addMonths,
  subMonths,
  format,
  isToday,
  parseISO,
} from 'date-fns';
import { tr } from 'date-fns/locale';
import toast from 'react-hot-toast';

// Mood configuration
export const MOODS = {
  great: { emoji: '😄', label: 'Harika', color: 'emerald' },
  good: { emoji: '😊', label: 'İyi', color: 'green' },
  neutral: { emoji: '😐', label: 'Normal', color: 'yellow' },
  bad: { emoji: '😕', label: 'Kötü', color: 'orange' },
  awful: { emoji: '😢', label: 'Berbat', color: 'red' },
};

// Category configuration
export const CATEGORIES = {
  win: { emoji: '✅', label: 'Başarılar', color: 'emerald' },
  struggle: { emoji: '⚡', label: 'Zorluklar', color: 'orange' },
  learning: { emoji: '💡', label: 'Öğrenmeler', color: 'blue' },
  grateful: { emoji: '🙏', label: 'Minnettarlık', color: 'purple' },
};

export const useMoodMapStore = create(
  subscribeWithSelector((set, get) => ({
    // =====================================================
    // STATE
    // =====================================================

    // Data
    entries: [],
    yearlyEntries: [], // For heatmap view
    tags: [],
    streak: 0,
    moodDistribution: null,
    categoryStats: null,

    // Navigation
    currentMonth: new Date(),
    currentYear: new Date().getFullYear(),
    viewMode: 'monthly', // 'monthly' | 'yearly'

    // Selected entry (for editing)
    selectedDate: null,
    selectedEntry: null,

    // Loading states
    isLoading: false,
    isLoadingTags: false,
    isLoadingStreak: false,
    isParsing: false,

    // Modal states
    isDayModalOpen: false,
    isTagModalOpen: false,

    // =====================================================
    // ACTIONS: Navigation
    // =====================================================

    goToPreviousMonth: () => {
      const newMonth = subMonths(get().currentMonth, 1);
      set({ currentMonth: newMonth });
      get().loadEntries();
    },

    goToNextMonth: () => {
      const newMonth = addMonths(get().currentMonth, 1);
      set({ currentMonth: newMonth });
      get().loadEntries();
    },

    goToToday: () => {
      set({ currentMonth: new Date() });
      get().loadEntries();
    },

    setViewMode: (mode) => {
      set({ viewMode: mode });
      if (mode === 'yearly') {
        get().loadYearlyEntries();
      }
    },

    setCurrentYear: (year) => {
      set({ currentYear: year });
      if (get().viewMode === 'yearly') {
        get().loadYearlyEntries();
      }
    },

    // =====================================================
    // ACTIONS: Data Loading
    // =====================================================

    loadEntries: async () => {
      set({ isLoading: true });
      try {
        const { currentMonth } = get();
        const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
        const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

        const entries = await moodmap.getEntries(startDate, endDate);
        set({ entries, isLoading: false });

        // Also load stats
        get().loadMoodDistribution();
        get().loadCategoryStats();
      } catch (error) {
        console.error('Error loading entries:', error);
        toast.error('Kayıtlar yüklenemedi');
        set({ isLoading: false });
      }
    },

    loadYearlyEntries: async () => {
      set({ isLoading: true });
      try {
        const { currentYear } = get();
        const entries = await moodmap.getAllEntries(currentYear);
        set({ yearlyEntries: entries, isLoading: false });
      } catch (error) {
        console.error('Error loading yearly entries:', error);
        toast.error('Yıllık kayıtlar yüklenemedi');
        set({ isLoading: false });
      }
    },

    loadTags: async () => {
      set({ isLoadingTags: true });
      try {
        const tags = await moodmap.getTags();
        set({ tags, isLoadingTags: false });
      } catch (error) {
        console.error('Error loading tags:', error);
        set({ isLoadingTags: false });
      }
    },

    loadStreak: async () => {
      set({ isLoadingStreak: true });
      try {
        const streak = await moodmap.getStreak();
        set({ streak, isLoadingStreak: false });
      } catch (error) {
        console.error('Error loading streak:', error);
        set({ isLoadingStreak: false });
      }
    },

    loadMoodDistribution: async () => {
      try {
        const { currentMonth } = get();
        const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
        const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
        const distribution = await moodmap.getMoodDistribution(startDate, endDate);
        set({ moodDistribution: distribution });
      } catch (error) {
        console.error('Error loading mood distribution:', error);
      }
    },

    loadCategoryStats: async () => {
      try {
        const { currentMonth } = get();
        const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
        const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
        const stats = await moodmap.getCategoryStats(startDate, endDate);
        set({ categoryStats: stats });
      } catch (error) {
        console.error('Error loading category stats:', error);
      }
    },

    // Initialize all data on mount
    initialize: async () => {
      await Promise.all([
        get().loadEntries(),
        get().loadTags(),
        get().loadStreak(),
      ]);
    },

    // =====================================================
    // ACTIONS: Entry Management
    // =====================================================

    selectDate: async (date) => {
      const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
      set({ selectedDate: dateStr, isLoading: true });

      try {
        const entry = await moodmap.getEntryByDate(dateStr);
        set({ selectedEntry: entry, isLoading: false, isDayModalOpen: true });
      } catch (error) {
        console.error('Error loading entry:', error);
        set({ selectedEntry: null, isLoading: false, isDayModalOpen: true });
      }
    },

    closeDayModal: () => {
      set({ isDayModalOpen: false, selectedDate: null, selectedEntry: null });
    },

    saveMood: async (mood) => {
      const { selectedDate, selectedEntry } = get();
      if (!selectedDate) return;

      try {
        if (selectedEntry) {
          // Update existing entry
          const updated = await moodmap.updateEntry(selectedEntry.id, { mood });
          set({ selectedEntry: { ...selectedEntry, mood: updated.mood } });
        } else {
          // Create new entry
          const created = await moodmap.upsertEntry(selectedDate, { mood });
          set({ selectedEntry: { ...created, items: [] } });
        }

        // Reload entries and stats
        get().loadEntries();
        get().loadStreak();
      } catch (error) {
        console.error('Error saving mood:', error);
        toast.error('Mood kaydedilemedi');
      }
    },

    addItem: async (category, content, tagId = null) => {
      const { selectedDate, selectedEntry } = get();
      if (!selectedDate) return;

      try {
        let entry = selectedEntry;

        // Create entry if it doesn't exist
        if (!entry) {
          entry = await moodmap.upsertEntry(selectedDate, {});
          set({ selectedEntry: { ...entry, items: [] } });
        }

        // Add item
        const item = await moodmap.addItem(entry.id, {
          category,
          content,
          tag_id: tagId,
        });

        // Update local state
        const updatedEntry = {
          ...entry,
          items: [...(entry.items || []), item],
        };
        set({ selectedEntry: updatedEntry });

        // Reload entries
        get().loadEntries();
        get().loadCategoryStats();

        return item;
      } catch (error) {
        console.error('Error adding item:', error);
        toast.error('Madde eklenemedi');
        throw error;
      }
    },

    deleteItem: async (itemId) => {
      const { selectedEntry } = get();
      if (!selectedEntry) return;

      try {
        await moodmap.deleteItem(itemId);

        // Update local state
        const updatedEntry = {
          ...selectedEntry,
          items: selectedEntry.items.filter((i) => i.id !== itemId),
        };
        set({ selectedEntry: updatedEntry });

        // Reload entries
        get().loadEntries();
        get().loadCategoryStats();
      } catch (error) {
        console.error('Error deleting item:', error);
        toast.error('Madde silinemedi');
      }
    },

    deleteEntry: async () => {
      const { selectedEntry } = get();
      if (!selectedEntry) return;

      try {
        await moodmap.deleteEntry(selectedEntry.id);
        set({ selectedEntry: null, isDayModalOpen: false });
        get().loadEntries();
        get().loadStreak();
        toast.success('Kayıt silindi');
      } catch (error) {
        console.error('Error deleting entry:', error);
        toast.error('Kayıt silinemedi');
      }
    },

    // =====================================================
    // ACTIONS: AI Parsing
    // =====================================================

    parseInput: async (text) => {
      const { tags, selectedDate, selectedEntry } = get();
      if (!text.trim()) return;

      set({ isParsing: true });

      try {
        // Get tag names for AI matching
        const tagNames = tags.map((t) => ({
          name: t.name,
          emoji: t.emoji,
          category: t.category,
        }));

        const result = await parseMoodMapInput(text, tagNames);

        if (!result || !result.items || result.items.length === 0) {
          toast.error('Giriş anlaşılamadı');
          set({ isParsing: false });
          return;
        }

        // Ensure entry exists
        let entry = selectedEntry;
        if (!entry) {
          entry = await moodmap.upsertEntry(selectedDate, {});
          set({ selectedEntry: { ...entry, items: [] } });
        }

        // Add all parsed items
        const items = [];
        for (const item of result.items) {
          // Find matching tag if suggested
          let tagId = null;
          if (item.suggestedTag) {
            const matchingTag = tags.find(
              (t) => t.name.toLowerCase() === item.suggestedTag.toLowerCase()
            );
            if (matchingTag) {
              tagId = matchingTag.id;
            }
          }

          const addedItem = await moodmap.addItem(entry.id, {
            category: item.category,
            content: item.content,
            tag_id: tagId,
          });
          items.push(addedItem);
        }

        // Update mood if suggested and not already set
        if (result.suggestedMood && !entry.mood) {
          await moodmap.updateEntry(entry.id, { mood: result.suggestedMood });
        }

        // Reload entry
        const updatedEntry = await moodmap.getEntryByDate(selectedDate);
        set({ selectedEntry: updatedEntry, isParsing: false });

        // Reload lists
        get().loadEntries();
        get().loadStreak();
        get().loadCategoryStats();

        toast.success(`${items.length} madde eklendi`);
      } catch (error) {
        console.error('Error parsing input:', error);
        toast.error('Giriş işlenemedi');
        set({ isParsing: false });
      }
    },

    // =====================================================
    // ACTIONS: Tag Management
    // =====================================================

    createTag: async (tagData) => {
      try {
        const tag = await moodmap.createTag(tagData);
        set({ tags: [...get().tags, tag] });
        toast.success('Etiket oluşturuldu');
        return tag;
      } catch (error) {
        console.error('Error creating tag:', error);
        toast.error('Etiket oluşturulamadı');
        throw error;
      }
    },

    deleteTag: async (tagId) => {
      try {
        await moodmap.deleteTag(tagId);
        set({ tags: get().tags.filter((t) => t.id !== tagId) });
        toast.success('Etiket silindi');
      } catch (error) {
        console.error('Error deleting tag:', error);
        toast.error('Etiket silinemedi');
      }
    },

    openTagModal: () => set({ isTagModalOpen: true }),
    closeTagModal: () => set({ isTagModalOpen: false }),

    // =====================================================
    // HELPERS
    // =====================================================

    getEntryForDate: (date) => {
      const { entries } = get();
      const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
      return entries.find((e) => e.date === dateStr);
    },

    getSmartTags: (category = null, limit = 8) => {
      const { tags } = get();
      let filtered = tags;

      if (category) {
        filtered = tags.filter(
          (t) => t.category === category || t.category === 'general'
        );
      }

      // Sort by usage count, then by system tags first
      return filtered
        .sort((a, b) => {
          // User tags with usage first
          if (a.usage_count !== b.usage_count) {
            return (b.usage_count || 0) - (a.usage_count || 0);
          }
          // Then system tags
          if (a.is_system !== b.is_system) {
            return a.is_system ? -1 : 1;
          }
          return 0;
        })
        .slice(0, limit);
    },

    formatDate: (date) => {
      const d = typeof date === 'string' ? parseISO(date) : date;
      if (isToday(d)) {
        return 'Bugün';
      }
      return format(d, 'd MMMM yyyy', { locale: tr });
    },
  }))
);

export default useMoodMapStore;
